// app/javascript/components/store/index.js

import Vue from 'vue';
import Vuex from 'vuex';
import moment from 'moment';
import VuexPersist from 'vuex-persist';

import api from './api';
import helpers from '../helpers';
import sliderValues from '../formulator/slider-values';
import baggedOrder from '../formulator/bagged-order';
import productTons from '../product-tons';

import order from './modules/order';
import recipe from './modules/recipe';
import formulator from './modules/formulator';

// ---------- BLINDAJES / DEFAULTS SEGUROS ----------
const safeWindow = (typeof window !== 'undefined') ? window : {};

// Asegura objeto y booleans para no explotar si no existe siteVariables todavía
const siteVars = (safeWindow.siteVariables && typeof safeWindow.siteVariables === 'object')
  ? safeWindow.siteVariables
  : {};

const windowLoggedIn = !!siteVars.loggedIn;
const enablePersist = !!siteVars.enablePersist;
const enabledMultiparticleFormat = !!siteVars.enabledMultiparticleFormat;

// sessionStorage puede no existir en algunos contextos (SSR/tests)
const safeSessionStorage = (() => {
  try {
    return safeWindow.sessionStorage || null;
  } catch (_) {
    return null;
  }
})();

// ---------------------------------------------------

const IVA_PERCENTAGE = 0.19;
const FULL_PERCENTAGE = 100;
// La urea tiene un comportamiento de slider distinto en todos los formuladores:
// se mueve de 0.1 en 0.1, se salta de 0 a 0.5 y no admite edición manual.
const UREA_NAME = 'Urea';
const UREA_INTERVAL = 0.1;
const isUrea = (ingredient) => ingredient.name === UREA_NAME;

function toNumberOr(value, fallback) {
  const parsed = parseFloat(value);

  return Number.isNaN(parsed) ? fallback : parsed;
}

// Función auxiliar para calcular el peso total de sacos
function calculateTotalSackWeight(packaging, totalSackWeight) {
  // console.log("PACKAGING:", packaging);
  // console.log("TOTAL SACK WEIGHT:", totalSackWeight);
  if (packaging === 'bulk' ){
    // console.log("Using bulk packaging, returning totalSackWeight as is.");
    return totalSackWeight;
  }

  const KILOGRAMS_PER_TON = 1000;

  const baggedKilograms = productTons.baggedKilograms(packaging);
  if (baggedKilograms) return baggedKilograms;

  // Solo aplicar la conversión si no es una promoción específica y el peso es menor a 1000
  if (totalSackWeight && totalSackWeight < KILOGRAMS_PER_TON) {
    return totalSackWeight * KILOGRAMS_PER_TON;
  }

  return totalSackWeight;
}

Vue.use(Vuex);

// VuexPersist solo si podemos persistir de forma segura y existe sessionStorage
const vuexLocalStorage = (enablePersist && safeSessionStorage)
  ? new VuexPersist({
      key: 'vuex',
      storage: safeSessionStorage,
      reducer: (state) => ({
        recipe: state.recipe,
        formulator: state.formulator,
        loggedIn: state.loggedIn,
      }),
      saveState: (key, state, storage) => {
        if (state.loggedIn) {
          storage.setItem(key, JSON.stringify(state));
        } else {
          storage.removeItem(key);
        }
      },
      restoreState: (key, storage) => {
        let state = storage.getItem(key);
        if (!state) return {};

        state = typeof state === 'string' ? JSON.parse(state) : state;
        const { loggedIn: _storageLoggedIn, ...stateWithoutLoggedIn } = state;

        // Si el usuario NO está logueado según variables de ventana actuales, invalidamos el storage
        if (!windowLoggedIn) {
          storage.removeItem(key);
          return {};
        }

        // Remap retired Coagra tab ('coagra') to the unified Alisur tab
        // ('ingredient') so a stale persisted session does not point at a tab
        // that no longer exists in `formulator-input.vue`.
        if (stateWithoutLoggedIn?.formulator?.selectedTab === 'coagra') {
          stateWithoutLoggedIn.formulator.selectedTab = 'ingredient';
        }

        return stateWithoutLoggedIn;
      },
    })
  : null;

const vuexPlugins = vuexLocalStorage ? [vuexLocalStorage.plugin] : [];

const store = new Vuex.Store({
  state: {
    ingredients: {},
    products: [],
    item: {
      form: 'powder',
      packaging: 'bulk',
    },
    showOverlay: false,
    alisurCoords: { lat: -40.484364, lng: -73.029934 },
    via: null,
    nextPath: '/',
    baseSackServicePrice: 0,
    firstRecipeLoaded: false,
    highPrecisionGoals: {
      expectedMilk: 0,
      producedMilkEm: 0,
      producedMilkPm: 0,
      expectedDryMatter: 0,
      givenDryMatter: 0,
      carbonEmission: 0,
    },
    showGoals: false,
    commission: null,
    onlinePayCommission: 0.015,
    depositPayCommission: 0.01,
    loggedIn: windowLoggedIn, // seguro si no existe siteVariables (queda false)
    settingsLimits: {
      grain_limit_without_binder: 0,
      min_binder_percentage: 0,
      min_salt_with_binder: 0,
      fibrous_ingredients_limit: 0,
      protein_ingredients_limit: 0,
      formulator_minimum_order_tons: 5,
      min_bagged_powder_density: 0,
    },
    // Guardamos aquí el flag para no depender de una variable libre en getters
    features: {
      enabledMultiparticleFormat: enabledMultiparticleFormat,
    },
  },
  mutations: {
    setIngredients: (state, payload) => {
      state.ingredients = payload;
    },
    updateOrder: (state, payload) => {
      state.order = payload;
    },
    updateShowOverlay: (state, payload) => {
      state.showOverlay = payload;
    },
    updateVia: (state, payload) => {
      state.via = payload;
    },
    updateSackServiceBasePrice: (state, payload) => {
      state.baseSackServicePrice = parseFloat(payload);
    },
    updateFirstRecipeLoaded: (state, payload) => {
      state.firstRecipeLoaded = payload;
    },
    updateHighPrecisionGoals: (state, payload) => {
      state.highPrecisionGoals = payload;
    },
    updateShowGoals: (state, payload) => {
      state.showGoals = payload;
    },
    updateCommission: (state, payload) => {
      state.commission = Number(payload);
    },
    updateLoggedIn: (state, payload) => {
      state.loggedIn = !!payload;
    },
    updateSettingsLimits: (state, payload) => {
      state.settingsLimits = {
        grain_limit_without_binder: parseFloat(payload.grain_limit_without_binder) || 0,
        min_binder_percentage: parseFloat(payload.min_binder_percentage) || 0,
        min_salt_with_binder: parseFloat(payload.min_salt_with_binder) || 0,
        fibrous_ingredients_limit: parseFloat(payload.fibrous_ingredients_limit) || 0,
        protein_ingredients_limit: parseFloat(payload.protein_ingredients_limit) || 0,
        formulator_minimum_order_tons: parseInt(payload.formulator_minimum_order_tons, 10) || 5,
        min_bagged_powder_density: parseFloat(payload.min_bagged_powder_density) || 0,
      };
    },
    // Por si quieres modificar el feature flag en runtime
    setFeatureFlag(state, { key, value }) {
      if (!state.features) state.features = {};
      state.features[key] = !!value;
    },
  },
  actions: {
    setIngredients: ({ commit, dispatch, state }, ingredients) => {
      // Dos límites por ingrediente:
      // - max_percentage (Max% formulador, área comercial): solo Alisur y Colun.
      // - max_inclusion_percentage (Max% inclusión, área de producción): todos
      //   los formuladores.
      // Coagra es dueño de sus propias materias primas y maneja otros criterios
      // comerciales, por lo que el Max% formulador no lo afecta: formula libre
      // hasta el Max% inclusión en TODAS las categorías (energéticos, fibrosos,
      // proteicos, minerales, aditivos y aglomerantes).
      const isCoagraWeb = !!state.order.isCoagraWeb;
      const freeCategoriesForCoagra = [
        'grain', 'floured', 'protein', 'salt_alisur', 'additive', 'binder',
      ];

      const ingredientsByName = ingredients.reduce((ingredientsHash, ingredient) => {
        const urea = isUrea(ingredient);
        // La urea siempre arranca en 0 y respeta la grilla de salto (0, 0.5, ...).
        const min = urea ? 0 : toNumberOr(ingredient.min_percentage, 0);
        const formulatorMax = toNumberOr(ingredient.max_percentage, FULL_PERCENTAGE);
        const inclusionMax = toNumberOr(ingredient.max_inclusion_percentage, FULL_PERCENTAGE);
        const isFreeForCoagra = isCoagraWeb && freeCategoriesForCoagra.includes(ingredient.category);
        const max = isFreeForCoagra ? inclusionMax : Math.min(formulatorMax, inclusionMax);
        const range = max - min + 1;
        // La urea: paso fijo 0.1, salto 0 -> 0.5 y valores ajustados a la grilla
        // para que los handles del slider siempre se posicionen.
        const interval = urea ? UREA_INTERVAL : helpers.stepSizeForRange(range);
        const defaultValue = Math.min(toNumberOr(ingredient.default_percentage, 0), max);
        const value = urea ? sliderValues.snapToSkipValue(defaultValue, max) : defaultValue;
        const rangeValue = urea ? [0, sliderValues.snapToSkipValue(max, max)] : [min, max];

        ingredientsHash[ingredient.name] = {
          ...ingredient,
          min,
          max,
          value,
          rangeValue,
          interval,
          forceSkip: urea,
        };

        return ingredientsHash;
      }, {});
      commit('setIngredients', ingredientsByName);
      dispatch('setIngredientRanges');
      dispatch('setIngredientFormulatorValues');
    },
    createOrder: (context) => {
      return api.createOrder(context.getters.order)
        .then((response) => {
          const link = response.transbankData?.url || response.order;
          const redirectLink = context.state.order.isColunWeb && !response.transbankData?.url
            ? `${link}${link.includes('?') ? '&' : '?'}tenant=colun`
            : link;

          window.location.href = redirectLink;
        });
    },
    setDateAvailabilityAndFees({ commit }, dateAvailabilityAndFees) {
      commit('updateDisabledDates', dateAvailabilityAndFees.disabled_dates);
      commit('updateDatesWithFees', dateAvailabilityAndFees.dates_with_fees);
      commit('updateLastDate', dateAvailabilityAndFees.last_date);
    },
    updateFreightService: context => {
      const lat = context.state.order.address.marker.position.lat;
      const lng = context.state.order.address.marker.position.lng;
      const productId = new URL(window.location.href).searchParams.get('product');

      // Acceso defensivo al packaging para manejar cuando viene de URL
      const packagingState = context.state.recipe.finishing.packaging;
      const packaging = typeof packagingState === 'string' ? packagingState : packagingState?.value;
      const originalWeight = productId
        ? (context.state.recipe.selectedTons || context.state.recipe.finishing.totalSackWeight)
        : context.state.recipe.finishing.totalSackWeight;

      const totalSackWeight = calculateTotalSackWeight(packaging, originalWeight);

      api.getOrderFreightValue({ lat, lng, productId, packaging, totalSackWeight })
        .then((value) => {
          context.commit('updateContactUserServiceFreight', value);
        });
    },
    setProductDetails({ commit, getters }, product) {
      const selectedTons = productTons.positiveTons(product.selected_tons, product.unit_tons || 15);
      let packagingValue = product.packaging;
      if (packagingValue === 'bagged25' || (packagingValue && packagingValue.startsWith('bagged25'))) {
        packagingValue = productTons.baggedPackagingForTons(selectedTons);
      }
      const packaging = getters.extendedPackagingList.find(e => e.value === packagingValue)
        || getters.productPackagingList.find(e => e.value === packagingValue)
        || {
          value: packagingValue,
          label: helpers.translate(packagingValue) || helpers.translate(product.packaging) || packagingValue,
          shortLabel: helpers.translate(packagingValue) || helpers.translate(product.packaging) || packagingValue,
        };

      commit('updateForm', getters.allTranslatedForms.find(e => e.value === product.form));
      commit('updatePackaging', packaging);
      commit('updateTotalSackWeight', selectedTons);
      commit('updateProductInformation', product);
      commit('updateProductIngredient', product);
      commit('updateFormulaName', product.name);
    },
    updateViewMode: (context, payload) => {
      context.commit('updateViewMode', payload);
    },
    updateFormulaName: (context, payload) => {
      context.commit('updateFormulaName', payload);
    },
    updateWayToPay: (context, payload) => {
      context.commit('updateWayToPay', payload);
    },
    updateForm: (context, payload) => {
      context.commit('updateForm', payload);
    },
    updatePackaging: (context, payload) => {
      context.commit('updatePackaging', payload);
    },
    updateShowOverlay: (context, payload) => {
      context.commit('updateShowOverlay', payload);
    },
    setUpdated: (context, payload) => {
      context.commit('setUpdated', payload);
    },
    updateVia: (context, payload) => {
      context.commit('updateVia', payload);
    },
    updateSackServiceBasePrice: (context, payload) => {
      context.commit('updateSackServiceBasePrice', payload);
    },
    updateHighPrecisionGoals: (context, payload) => {
      context.commit('updateHighPrecisionGoals', payload);
    },
    updateShowGoals: (context, payload) => {
      context.commit('updateShowGoals', payload);
    },
  },
  getters: {
    params: (state) => {
      if (state.recipe.type !== 'product') {
        const queryObject = {};
        const keys = Object.keys(state.recipe.ingredientsProportion);
        for (let i = 0; i < keys.length; i++) {
          const ingredientName = keys[i];
          const ingredientId = state.recipe.ingredientIdsByName[ingredientName] || state.ingredients[ingredientName]?.id;

          if (ingredientId) {
            queryObject[ingredientId] = state.recipe.ingredientsProportion[ingredientName];
          }
        }
        return queryObject;
      }
      return null;
    },
    googleMapUrl: state => {
      if (state.order.address.reference === null) {
        return '/';
      }
      const ref = state.order.address.reference.split(',')[0];
      const marker = state.order.address.marker;
      return `https://www.google.cl/maps/place/${ref}/@${marker.position.lat},${marker.position.lng},17z`;
    },
    order: (state, getters) => {
      const productId = state.recipe.details === null ? null : state.recipe.details.id;
      const item = {
        'type': state.recipe.type,
        'finishing': state.recipe.finishing,
        'product_id': productId,
        'ingredients': getters.ingredientsWithProportionsForOrder,
        'nutritional_info': state.recipe.nutritionalInfo,
        'tons': state.recipe.type === 'product'
          ? (state.recipe.selectedTons || state.recipe.finishing.totalSackWeight)
          : state.recipe.finishing.totalSackWeight,
      };
      const orderData = {
        'item': item,
        'user': state.order.user,
        'address': state.order.address,
        'date': state.order.desiredDate,
        'way_to_pay': state.order.wayToPay,
        'via': state.via,
        'formula_name': state.order.formulaName,
        'physiological_state': state.order.physiologicalState,
        'has_sack_service': getters.sackServiceValue > 0,
        'dairy_info': state.formulator.dairyInfo,
      };
      if (state.order.modifyOrderId) {
        orderData.modify_order_id = state.order.modifyOrderId;
      }
      return orderData;
    },
    subTotal: (state) => {
      return state.order.isCoagraWeb ? 0 : state.recipe.price * state.recipe.weight;
    },
    serviceDateValue: (state) => (state.order.isCoagraWeb ? 0 : state.order.services.date),
    serviceFreightValue: (state) => (state.order.isCoagraWeb ? 0 : state.order.services.freight),
    sackServiceValue: (state) => (state.order.isCoagraWeb ? 0 : state.order.services.sackDischarge),
    netTotalWithoutSalesService: (_state, getters) => (
      getters.subTotal + getters.serviceFreightValue + getters.serviceDateValue + getters.sackServiceValue
    ),
    finalCommission(state) {
      if (state.order.wayToPay === 'online') {
        return state.onlinePayCommission + state.commission;
      } else if (state.order.wayToPay === 'deposit') {
        if (state.commission > state.depositPayCommission) {
          return state.commission - state.depositPayCommission;
        }
        return 0;
      }
      return state.commission;
    },
    salesService: (_state, getters) => (getters.netTotalWithoutSalesService * getters.finalCommission),
    netTotal: (_state, getters) => (getters.netTotalWithoutSalesService + getters.salesService),
    iva: (_state, getters) => (Math.round(getters.netTotal * IVA_PERCENTAGE)),
    totalPrice: (_state, getters) => (getters.netTotal + getters.iva),
    amountDue: (state, getters) => {
      const total = getters.totalPrice;
      if (!state.order.modifyOrderId) return Math.max(total, 0);

      const paid = Number(state.order.paidAmount) || 0;
      const original = Number(state.order.originalAmount) || 0;
      // Pedidos marcados pagados sin registros de Payment (p. ej. crédito):
      // se acredita el monto original para cobrar solo la diferencia.
      const credited = Math.max(paid, state.order.paymentCompleted ? original : 0);
      return Math.max(total - credited, 0);
    },
    isModifying: (state) => !!state.order.modifyOrderId,
    sackServiceBasePrice: state => (state.baseSackServicePrice),
    orderItemFinishingType: state => (state.recipe.finishing),
    datePrice: (state) => (_date) => {
      if (!_date) return undefined;
      let price = 0;
      Object.keys(state.order.datesWithFees).forEach(fee => {
        const dateRange = state.order.datesWithFees[fee];
        const start = dateRange.start_date;
        const end = dateRange.end_date;
        if (moment(_date).isSameOrAfter(start) && moment(_date).isSameOrBefore(end)) {
          price += Number(fee);
        }
      });
      return price;
    },
    dateArrayWithFees: state => {
      const dates = [];
      const disabledDates = state.order.disabledDates.map(date => new Date(date).toString());
      Object.keys(state.order.datesWithFees).forEach(fee => {
        const dateRange = state.order.datesWithFees[fee];
        const end = moment(dateRange.end_date);
        let start = moment(dateRange.start_date);
        while (start <= end) {
          const isDisableDate = disabledDates.includes(start.toDate().toString());
          if (!isDisableDate) {
            dates.push(start.toDate());
          }
          start = moment(start).add(1, 'days');
        }
      });
      return dates;
    },
    isValidOrder: state => {
      const form = state.order;
      const user = form.user;
      const validUserForm = !!user.name && !!user.surname && !!user.numberPrincipal &&
        !!user.bname && !!user.rut && !!user.bcategory && !!user.baddress;
      const validMap = !isNaN(parseInt(form.services.freight, 10)) && form.address.name && form.address.reference;
      const validDate = !!form.desiredDate;
      if (validUserForm && validMap && validDate && form.viewMode) {
        return true;
      }
      return false;
    },
    ingredientsArray(state) {
      return Object.values(state.ingredients);
    },
    generalIngredients(_state, getters) {
      return getters.ingredientsArray.filter(ingredient =>
        ['grain', 'floured', 'protein'].includes(ingredient.category),
      );
    },
    saltIngredients(_state, getters) {
      return getters.ingredientsArray.filter(ingredient => ingredient.category === 'salt_alisur');
    },
    AdditiveIngredients(_state, getters) {
      return getters.ingredientsArray.filter(ingredient =>
        ['additive', 'binder'].includes(ingredient.category),
      );
    },
    formulatorIngredients(_state, getters) {
      return getters.generalIngredients.concat(getters.saltIngredients).concat(getters.AdditiveIngredients);
    },
    ingredientsForSelect: (_state, getters) => (ingredientType) => {
      const ingredientsArray = [];
      getters.ingredientsArray.forEach(ingredient => {
        ingredientsArray.push({
          category: ingredient.category,
          value: ingredient.id,
          label: ingredient.name,
          defaultPercentage: ingredient.value,
          ms: ingredient.ms,
          minPercentage: ingredient.min,
          maxPercentage: ingredient.max,
        });
      });
      return ingredientsArray.filter(ingredient =>
        ingredient.category === ingredientType || ingredientType.includes(ingredient.category));
    },
    ingredientsWithProportionsForOrder(state) {
      return Object.entries(state.recipe.ingredientsProportion).reduce((ingredients, [name, proportion]) => {
        if (state.ingredients[name]) {
          ingredients.push({ id: state.ingredients[name].id, value: proportion });
        }
        return ingredients;
      }, []);
    },
    // Formulador web: granel y ensacado. Maxisacos solo en productos de catálogo.
    // El mínimo de ensacado sale de Ajustes de Alisur (Alisur y Colun). Coagra sigue en 5 ton.
    extendedPackagingList(state) {
      const options = [
        { value: 'bulk', label: helpers.translate('bulk') },
      ];
      const minTons = baggedOrder.formulatorBaggedMinTons({
        isCoagraWeb: !!state.order.isCoagraWeb,
        minimumOrderTons: state.settingsLimits.formulator_minimum_order_tons,
      });
      return options.concat(baggedOrder.baggedPackagingOptions(minTons));
    },
    allTranslatedForms() {
      const forms = ['powder', 'multiparticle', 'pellet12', 'pellet8', 'pellet6', 'pellet3', 'whole', 'ground'];
      return helpers.translateList(forms);
    },
    pelletForms() {
      return ['pellet12', 'pellet8', 'pellet6', 'pellet3'];
    },
    translatedFormsForFormulatorSelect: (state) => {
      const values = baggedOrder.formulatorFormValues({
        packaging: state.recipe.finishing.packaging,
        includeMultiparticle: !!state.features?.enabledMultiparticleFormat,
      });

      return helpers.translateList(values);
    },
    productPackagingList() {
      const packagings = ['bulk', 'bagged25', 'maxibag'];
      return helpers.translateList(packagings);
    },
    queryIngredients: (state, getters) => (salts) => {
      const saltsFilter = ['salt_alisur', 'additive', 'binder'];
      const query = getters.generalIngredients
        .concat(getters.saltIngredients)
        .filter(ingredient => !saltsFilter.includes(ingredient.category))
        .map(ingredient => ({ id: ingredient.id, value: ingredient.value }));

      salts.forEach(salt => {
        if (salt.id && salt.amount > 0.0) {
          query.push({ id: salt.id, value: salt.amount });
        }
      });
      return query;
    },
    initialized: state => state.recipe.type !== '',
  },
  modules: {
    order,
    recipe,
    formulator,
  },
  plugins: vuexPlugins,
});

export default store;
